import {
  ArrowLeftOutlined,
  DollarCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Tabs,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import {
  showCautionConfirm,
  showDestructiveConfirm,
} from "components/modals/destructiveConfirm";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import PageHeading from "components/PageHeading.component";
import CardSectionTitle from "components/CardSectionTitle.component";
import { ReadOnlyWhenRestricted } from "components/capability";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import {
  useCreateProduct,
  useDeleteProduct,
  useUpdateProduct,
  ProductWriteForm,
} from "hooks/mutations/product";
import { getProductById } from "hooks/queries/product";
import { useTabParam } from "hooks/useTabParam";
import ProductHolders, { holdersTitle, useProductHolders } from "./ProductHolders";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import {
  AccessScope,
  CatalogVisibility,
  ClassCategory,
  ExpiryUnit,
  PackageProduct,
  ProductType,
} from "shared/types";
import { localTzDate } from "shared/utils/dayjs";
import { formatCurrency } from "shared/utils";
import { textPx } from "shared/constants/zoom";

const DATE_SUBMIT_FORMAT = "YYYY-MM-DD";

// Function, not a module-level const: translate() must run after i18n init.
const expiryUnitOptions = () => [
  { value: ExpiryUnit.Day, label: translate("expiry_unit.day") },
  { value: ExpiryUnit.Month, label: translate("expiry_unit.month") },
  { value: ExpiryUnit.Year, label: translate("expiry_unit.year") },
];

// Prototype label style: red asterisk AFTER the label text.
const requiredMarkAfter = (label: ReactNode, { required }: { required: boolean }) => (
  <>
    {label}
    {required && <span style={{ color: colors.red, marginLeft: 4 }}>*</span>}
  </>
);

// Soft-red delete: red text on a light red tint, never a solid danger fill.
// Shared by the membership and credit footers so both read the same.
const softDeleteStyle = {
  background: colors.errorTint,
  color: colors.red,
  borderColor: "transparent",
};

// Rp 0 is a real price (a free trial, a complimentary pack; Andrew,
// 2026-09-25), so 0 must SHOW as "Rp 0" and a cleared field must stay EMPTY:
// the old parser turned an empty field into 0, which the min: 1 rule used to
// catch. With 0 allowed, only an explicit 0 may save a free product; a blank
// price still fails `required`.
const currencyInputProps = {
  style: { width: "100%" } as const,
  formatter: (value?: number | string) =>
    value === undefined || value === null || value === ""
      ? ""
      : formatCurrency(Number(value)),
  parser: (value?: string) => {
    const digits = (value ?? "").replace(/\D/g, "");
    return (digits === "" ? "" : Number(digits)) as unknown as number;
  },
};

const mapProductToForm = (product: PackageProduct) => {
  const base = {
    name: product.name,
    description: product.description ?? undefined,
    class_ids: product.class_type.map((c) => c.id),
    is_promo: product.is_promo,
    // Boolean form field (a Switch wants one); onSubmit maps it back to the
    // CatalogVisibility enum.
    listed_in_client_catalog:
      product.client_catalog_visibility !== CatalogVisibility.Hidden,
    promo_starts_at: product.promo_starts_at
      ? localTzDate(product.promo_starts_at)
      : undefined,
    promo_ends_at: product.promo_ends_at
      ? localTzDate(product.promo_ends_at)
      : undefined,
    access_scope: product.access_scope ?? undefined,
  };
  if (product.product_type === ProductType.Membership) {
    const tier = product.tiers[0];
    return {
      ...base,
      price: tier?.price,
      expiry_value: tier?.expiry_value ?? tier?.expiry_duration_by_days,
      expiry_unit: tier?.expiry_unit ?? ExpiryUnit.Day,
      is_shareable: tier?.is_shareable ?? false,
      max_members: tier?.max_members ?? 2,
    };
  }
  return {
    ...base,
    tiers: product.tiers.map((tier) => ({
      id: tier.id,
      total_credits: tier.total_credits,
      price: tier.price,
      expiry_value: tier.expiry_value ?? tier.expiry_duration_by_days,
      expiry_unit: tier.expiry_unit ?? ExpiryUnit.Day,
    })),
    // Product-level in the form, stored per tier. `some` rather than `every`
    // so a product left half-flagged by an older build still reads as shared
    // and can be corrected in one save.
    is_shareable: product.tiers.some((tier) => tier.is_shareable),
    max_members:
      product.tiers.find((tier) => tier.is_shareable)?.max_members ?? 2,
  };
};

const { Text } = Typography;

// Promo start + end, shared by the credit and membership forms. The server
// runs the same two checks; these only catch them before the round trip, in
// the admin's language.
//  - the end may not come before the start: the end picker greys out every
//    day before the chosen start, and moving the start past the chosen end
//    moves the end along to the start day (Andrew, 2026-09-25);
//  - the end may not be in the past, on a new promo AND when editing one
//    (Andrew, 2026-09-25): an ended promo locks everything but its name, so a
//    past end would freeze it the moment it saves. A running promo is ended
//    early by setting today as its end. An ALREADY ended promo is locked and
//    exempt (`blockPastEnd` false), so it can still be renamed.
// Structural, not dayjs's Dayjs: AntD's DatePicker hands over its own bundled
// dayjs instance, whose type differs from the app's.
type PickedDate = { format: (template: string) => string };

const PromoDateFields = ({ blockPastEnd }: { blockPastEnd: boolean }) => {
  const form = Form.useFormInstance();
  const start: PickedDate | undefined = Form.useWatch("promo_starts_at", form);
  const wall = (d: PickedDate) => d.format(DATE_SUBMIT_FORMAT);
  const today = localTzDate().format(DATE_SUBMIT_FORMAT);
  // The earliest day the end may take: the start day, and never before today
  // either (YYYY-MM-DD strings compare as dates).
  const endFloor = [start ? wall(start) : null, blockPastEnd ? today : null]
    .filter((day): day is string => !!day)
    .sort()
    .pop();
  return (
    <Row gutter={12}>
      <Col span={12}>
        <Form.Item
          name="promo_starts_at"
          label={translate("product_catalog.label_promo_start")}
          rules={[{ required: true, message: translate("form.error.select") }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            onChange={(picked) => {
              const end: PickedDate | undefined = form.getFieldValue("promo_ends_at");
              if (!picked || !end) return;
              if (wall(end) < wall(picked)) {
                form.setFieldValue("promo_ends_at", picked);
              }
              // Re-check the end against the start as it now stands.
              // setFieldValue runs no validation, and the dependency check
              // the start change fires can land on an in-between value
              // (typing a date commits more than once), which left a stale
              // "end before start" error under two identical dates.
              form.validateFields(["promo_ends_at"]).catch(() => {});
            }}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="promo_ends_at"
          label={translate("product_catalog.label_promo_end")}
          dependencies={["promo_starts_at"]}
          rules={[
            { required: true, message: translate("form.error.select") },
            ({ getFieldValue }) => ({
              validator: (_, value?: PickedDate) => {
                const start: PickedDate | undefined = getFieldValue("promo_starts_at");
                if (value && start && wall(value) < wall(start)) {
                  return Promise.reject(
                    new Error(translate("product_catalog.promo_end_before_start")),
                  );
                }
                if (blockPastEnd && value && wall(value) < today) {
                  return Promise.reject(
                    new Error(translate("product_catalog.promo_end_in_past")),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            disabledDate={endFloor ? (d) => wall(d) < endFloor : undefined}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

// The dearest class among the selected ones, by the classes' own costs: every
// tier has to be able to pay for one booking of it (the server refuses a
// save otherwise, product_catalog.tier_cannot_afford_class).
const dearestClassCost = (
  classIds: string[] | undefined,
  costById: Map<string, number>,
) => Math.max(1, ...(classIds ?? []).map((id) => costById.get(id) ?? 1));

// Kredit per Booking (credit products only), READ-ONLY here: a class's cost is
// set on the class itself (Kelas page) and is the same on every package that
// covers it (Andrew's team, 2026-10-20). The same list the customer app
// prints on its package page (ProductDetailScreen's included-classes list):
// one bullet per class, the cost right after the name behind the dollar icon,
// dearest first, so the admin reads what the tiers below must be able to pay
// for and sees the package the way its buyers do.
const ClassCreditList = ({
  classIds,
  classNameById,
  costById,
}: {
  classIds: string[];
  classNameById: Map<string, string>;
  costById: Map<string, number>;
}) => {
  if (classIds.length === 0) return null;
  const costOf = (id: string) => costById.get(id) ?? 1;
  const ordered = [...classIds].sort(
    (a, b) =>
      costOf(b) - costOf(a) ||
      (classNameById.get(a) ?? "").localeCompare(classNameById.get(b) ?? ""),
  );
  return (
    <Form.Item label={translate("product_catalog.class_credits_title")}>
      <ul
        style={{
          margin: 0,
          padding: "0 0 0 20px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          color: colors.ink,
        }}
      >
        {ordered.map((id) => (
          <li key={id} style={{ overflowWrap: "anywhere" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>{classNameById.get(id) ?? "-"}</span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  whiteSpace: "nowrap",
                  color: colors.ink2,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <DollarCircleOutlined aria-hidden style={{ fontSize: textPx(13), color: colors.ink3 }} />
                {costOf(id) === 1
                  ? translate("customer_package.credit_cost_each_one")
                  : translateWithVars("customer_package.credit_cost_each", { n: costOf(id) })}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Form.Item>
  );
};

// One titled section of a product form: the card-section title the admin uses
// elsewhere, with a divider above every section but the first, so the form
// reads as four groups (what it is, what it covers, what it costs, how it is
// sold) instead of one run of fields (chris, 2026-10-21). The credit and the
// membership form split differently, because a membership has one price, no
// tiers and no credit list.
const FormSection = ({
  title,
  subtitle,
  first = false,
}: {
  title: string;
  subtitle?: string;
  first?: boolean;
}) => (
  <>
    {!first && <Divider style={{ margin: "8px 0 24px" }} />}
    <div style={{ marginBottom: 18 }}>
      <CardSectionTitle title={title} subtitle={subtitle} />
    </div>
  </>
);

const PackageProductDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const [form] = useForm();

  const { data: product, isLoading } = getProductById(productId);
  // Membership create and detail live under /membership (keeps the sidebar's
  // Membership item highlighted); the loaded product stays authoritative.
  // `?type=membership` on /package/new still works for old links.
  const isMembership =
    product?.product_type === ProductType.Membership ||
    (!product &&
      (location.pathname.startsWith(PATHS.MEMBERSHIP) ||
        searchParams.get("type") === ProductType.Membership));
  const productType = isMembership ? ProductType.Membership : ProductType.Credit;
  const isEdit = !!productId;

  // Every package of this product a client can still book with (both
  // types), on its own tab. Fetched on load so the tab label has the count.
  const holders = useProductHolders(isEdit ? productId : undefined);
  const { activeKey: pageTab, onChange: setPageTab } = useTabParam("detail", "tab", [
    "detail",
    "holders",
  ]);
  const { data: classes, isLoading: classLoading } = getAllClassTypeByBusinessId();
  const { mutate: createProduct, isPending: creating } = useCreateProduct();
  const { mutate: updateProduct, isPending: updating } = useUpdateProduct();
  const { mutate: deleteProduct } = useDeleteProduct();

  // No studio-wide gate: the product flag is the only one, so the control is
  // always visible. Hiding it behind a Pengaturan switch made the feature
  // undiscoverable, since the form showed nothing at all while that was off.
  //
  // 2 is the prefilled cap rather than a blank field. Couples are the headline
  // case, the range is only 2-5, and Save Changes is already disabled until the
  // form is dirty — a second, invisible reason for it to be dead would leave an
  // admin staring at a greyed button with no idea which field is at fault.
  const DEFAULT_MAX_MEMBERS = 2;
  const defaultMaxMembers = DEFAULT_MAX_MEMBERS;

  // Soft-deleting a tier that has been sold deserves a warning; sold counts
  // come from the loaded product.
  const soldCountByTierId = useMemo(() => {
    const map = new Map<number, number>();
    product?.tiers.forEach((tier) => map.set(tier.id, tier.sold_count));
    return map;
  }, [product]);

  // Simpan Perubahan stays disabled until something actually changes, same as
  // Detail Kelas and Profil Bisnis. setFieldsValue does not fire
  // onValuesChange, so loading the product below never counts as an edit.
  const [isFieldChanged, setIsFieldChanged] = useState(false);

  useEffect(() => {
    if (product) {
      form.setFieldsValue(mapProductToForm(product));
      // A refetch replaces the form with server values, so it is clean again.
      setIsFieldChanged(false);
    }
  }, [product, form]);

  // Duplikat prefill — set once on mount for a fresh create page navigated
  // to with `state: { duplicateFrom }` (see handleDuplicate below). Runs
  // after the form's own `initialValues`, overriding the plain-empty
  // create-mode defaults with the source product's values.
  useEffect(() => {
    if (isEdit) return;
    const duplicateFrom = (
      location.state as { duplicateFrom?: Record<string, unknown> } | null
    )?.duplicateFrom;
    if (duplicateFrom) form.setFieldsValue(duplicateFrom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classOptions = useMemo(() => {
    if (!classes) return [];
    const privateClasses = classes.filter(
      (cls) => cls.type === ClassCategory.Private,
    );
    const openClasses = classes.filter((cls) => cls.type === ClassCategory.Open);
    const options = [];
    if (privateClasses.length > 0) {
      options.push({
        label: "Private",
        title: "Private",
        options: privateClasses.map((cls) => ({ label: cls.name, value: cls.id })),
      });
    }
    if (openClasses.length > 0) {
      options.push({
        label: "Open",
        title: "Open",
        options: openClasses.map((cls) => ({ label: cls.name, value: cls.id })),
      });
    }
    return options;
  }, [classes]);

  // Class names for the Kredit per Booking rows: the studio's live classes,
  // then the product's own (a class no longer in the list still has a name).
  const classNameById = useMemo(() => {
    const map = new Map<string, string>();
    product?.class_type.forEach((cls) => map.set(cls.id, cls.name));
    classes?.forEach((cls) => map.set(cls.id, cls.name));
    return map;
  }, [classes, product]);
  // Each class's own cost (class_type.credit_cost): the live class list
  // first, then the product's own classes.
  const classCostById = useMemo(() => {
    const map = new Map<string, number>();
    product?.class_type.forEach((cls) => map.set(cls.id, cls.credit_cost ?? 1));
    classes?.forEach((cls) => map.set(cls.id, cls.credit_cost ?? 1));
    return map;
  }, [classes, product]);
  const watchedClassIds: string[] | undefined = Form.useWatch("class_ids", form);
  const watchedTiers: { total_credits?: number | null }[] | undefined =
    Form.useWatch("tiers", form);
  // Every tier must pay for one booking of the dearest selected class.
  const dearestCost = isMembership
    ? 1
    : dearestClassCost(watchedClassIds, classCostById);
  // Simpan stays greyed while any tier is short (the row says why in red),
  // rather than clickable with nothing happening (Andrew, 2026-10-19). Always
  // false on a membership (no costs there).
  const anyTierShort =
    !isMembership &&
    (watchedTiers ?? []).some(
      (tier) => typeof tier?.total_credits === "number" && tier.total_credits < dearestCost,
    );

  // Set when this page was opened from a class's own Paket/Membership tab
  // (ClassTypePackage.tsx), so backToCatalog can return there instead of the
  // main catalog — see the returnTo comment below.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  // Lands back where the admin actually came from. If returnTo is set (this
  // page was opened from a class's own Paket/Membership tab), that exact
  // page + tab wins. Otherwise falls back to the main catalog, on whichever
  // tab the product actually belongs to (Promo vs Reguler) — not always the
  // catalog's default Reguler tab, since a bare `navigate(PATHS.MEMBERSHIP)`
  // drops the ?tab= query and used to silently bounce the admin to Reguler,
  // hiding the very change they just made. Callers pass the product's own
  // is_promo (freshly submitted on save, or the loaded product's on delete/
  // cancel/back); omitted defaults to false, which is also correct for the
  // CREATE form's Batal/back button, where no product exists yet.
  const backToCatalog = (targetIsPromo = false) =>
    navigate(
      returnTo ??
        `${isMembership ? PATHS.MEMBERSHIP : PATHS.PACKAGE}?tab=${targetIsPromo ? "promo" : "reguler"}`,
    );

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        // A tier that can't pay for the dearest class: its row already says
        // so in red (the server refuses it too).
        if (!isMembership) {
          const dearest = dearestClassCost(values.class_ids, classCostById);
          const tierShort = (values.tiers ?? []).some(
            (tier: { total_credits?: number | null }) =>
              typeof tier?.total_credits === "number" && tier.total_credits < dearest,
          );
          if (tierShort) return;
        }
        const isPromo = !!values.is_promo;
        const productForm: ProductWriteForm = {
          name: values.name,
          description: values.description ?? null,
          product_type: productType,
          is_promo: isPromo,
          client_catalog_visibility:
            values.listed_in_client_catalog === false
              ? CatalogVisibility.Hidden
              : CatalogVisibility.Listed,
          promo_starts_at:
            isPromo && values.promo_starts_at
              ? values.promo_starts_at.format(DATE_SUBMIT_FORMAT)
              : null,
          promo_ends_at:
            isPromo && values.promo_ends_at
              ? values.promo_ends_at.format(DATE_SUBMIT_FORMAT)
              : null,
          access_scope: isMembership ? values.access_scope : null,
          class_ids:
            isMembership && values.access_scope === AccessScope.AllOpen
              ? []
              : values.class_ids,
          tiers: isMembership
            ? [
                {
                  // Keep the tier id on edit so the membership tier is
                  // updated in place (a sold membership keeps its FK).
                  id: product?.tiers[0]?.id,
                  price: values.price,
                  expiry_value: values.expiry_value,
                  expiry_unit: values.expiry_unit,
                  is_shareable: !!values.is_shareable,
                  max_members: values.is_shareable
                    ? values.max_members ?? defaultMaxMembers
                    : 1,
                },
              ]
            : // Regular and promo credit products alike carry every tier the
              // admin listed; a promo buyer upgrades between these sibling
              // tiers exactly like a regular buyer does.
              values.tiers?.map((tier: any) => ({
                ...tier,
                // One product-level decision applied to every tier.
                is_shareable: !!values.is_shareable,
                // Collapse to 1 when off so a stale cap can never take effect
                // if sharing is re-enabled later.
                max_members: values.is_shareable
                  ? values.max_members ?? defaultMaxMembers
                  : 1,
              })),
        };
        if (!isEdit || !productId) {
          createProduct(productForm, { onSuccess: () => backToCatalog(isPromo) });
          return;
        }
        const save = () =>
          updateProduct(
            { productId, productForm },
            { onSuccess: () => backToCatalog(isPromo) },
          );
        // A product's classes are read live, so removing one also stops every
        // client who already holds the product from booking it. Say so, with
        // how many people that is, before saving (System Review SD3). No
        // warning when nobody holds it: nothing changes for anyone.
        const holders = product?.active_holder_count ?? 0;
        const keptClassIds = new Set<string>(
          productForm.access_scope === AccessScope.AllOpen
            ? (classes ?? [])
                .filter((cls) => cls.type === ClassCategory.Open)
                .map((cls) => cls.id)
            : productForm.class_ids,
        );
        const removedClasses = (product?.class_type ?? []).filter(
          (cls) => !keptClassIds.has(cls.id),
        );
        if (holders === 0 || removedClasses.length === 0) {
          save();
          return;
        }
        showDestructiveConfirm({
          title: translate("product_catalog.remove_class_title"),
          message: translateWithVars(
            holders === 1
              ? "product_catalog.remove_class_content_one"
              : "product_catalog.remove_class_content_other",
            {
              n: holders,
              classes: removedClasses.map((cls) => cls.name).join(", "),
            },
          ),
          confirmLabel: translate("button.save_changes"),
          onConfirm: save,
        });
      })
      .catch(() => {});
  };

  const deleteLabel = translate(
    isMembership
      ? "product_catalog.delete_membership_confirm"
      : "product_catalog.delete_product_confirm",
  );

  const confirmDelete = () =>
    showDestructiveConfirm({
      title: translate(
        isMembership
          ? "product_catalog.delete_membership_title"
          : "product_catalog.delete_product_title",
      ),
      message: translate(
        isMembership
          ? "product_catalog.delete_membership_content"
          : "product_catalog.delete_product_content",
      ),
      confirmLabel: deleteLabel,
      onConfirm: () => {
        if (productId)
          deleteProduct(productId, {
            onSuccess: () => backToCatalog(!!product?.is_promo),
          });
      },
    });

  const title = isEdit
    ? (product?.name ?? translate("product_catalog.detail_title"))
    : translate(
        isMembership
          ? "product_catalog.new_membership_title"
          : "product_catalog.new_credit_title",
      );

  // Once a promo's own end date has passed, every field except its name is
  // locked (server-enforced too, see updateProduct) — reactivating the
  // window/price/tiers would let this row silently represent a second real
  // campaign. Use Duplikat instead to start a fresh, freely-editable product.
  const locked = isEdit && !!product?.promo_ended;
  // Pembatasan Admin: the detail stays viewable (viewing is open), every
  // control inside goes disabled via ReadOnlyWhenRestricted below. Every
  // control with an EXPLICIT `disabled` prop beats that context (name input,
  // promo switch, save / duplicate / delete buttons), so each reads this
  // flag directly. Andrew caught the promo switch still flipping on review.
  const { isRestricted } = useAdminCapabilities();
  const catalogRestricted = isRestricted(AdminCapability.CatalogPricing);

  const handleDuplicate = () => {
    if (!product) return;
    const prefill: Record<string, unknown> = {
      ...mapProductToForm(product),
      // Blank on purpose — copying stale past dates would create another
      // already-ended promo the moment it's saved; the admin must pick a
      // fresh window deliberately.
      promo_starts_at: undefined,
      promo_ends_at: undefined,
      // Always listed, even when the source is hidden: a duplicate is a fresh
      // product the admin is about to sell (typically the replacement for the
      // one being delisted), so inheriting "hidden" would be the wrong start.
      listed_in_client_catalog: true,
    };
    if (!isMembership && Array.isArray(prefill.tiers)) {
      // Strip tier ids so the duplicate creates brand-new tiers rather than
      // referencing the source product's (soft-deletable, sale-tracked) ones.
      prefill.tiers = (prefill.tiers as { id?: number }[]).map(
        ({ id: _id, ...rest }) => rest,
      );
    }
    navigate(isMembership ? PATHS.MEMBERSHIP_NEW : `${PATHS.PACKAGE_NEW}?type=credit`, {
      // Carry returnTo forward too — duplicating a class-scoped product
      // should still land the new copy back on that same class page.
      state: { duplicateFrom: prefill, returnTo },
    });
  };

  const tierFields = (
  <>
    <FormSection title={translate("product_catalog.tier_section")} />
    <Form.List
      name="tiers"
      rules={[
        {
          validator: (_, tiers) =>
            tiers && tiers.length > 0
              ? Promise.resolve()
              : Promise.reject(
                  new Error(translate("form.error.select")),
                ),
        },
        {
          // Tiers are named "{product} - {credits} Kredit", so two with
          // the same credit count would share one name everywhere a
          // tier is listed. The server refuses it too.
          validator: (_, tiers?: { total_credits?: number }[]) => {
            const credits = (tiers ?? [])
              .map((tier) => tier?.total_credits)
              .filter((credit) => credit != null);
            return new Set(credits).size === credits.length
              ? Promise.resolve()
              : Promise.reject(
                  new Error(translate("product_catalog.duplicate_credits")),
                );
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => {
        return (
          <Flex vertical gap={4}>
            {fields.map((field) => {
              const tierCredits = watchedTiers?.[field.name]?.total_credits;
              const tierShort =
                typeof tierCredits === "number" && tierCredits < dearestCost;
              return (
                <div key={field.key}>
                  <Row gutter={8} align="middle">
                    <Form.Item name={[field.name, "id"]} hidden>
                      <Input type="hidden" />
                    </Form.Item>
                    <Col span={5}>
                      <Form.Item
                        name={[field.name, "total_credits"]}
                        label={translate("label.number_of_sessions")}
                        rules={[
                          {
                            required: true,
                            type: "number",
                            min: 1,
                            message: translate("form.error.required"),
                          },
                        ]}
                      >
                        <InputNumber style={{ width: "100%" }} min={1} />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        name={[field.name, "price"]}
                        label={translate("label.price")}
                        rules={[
                          {
                            required: true,
                            type: "number",
                            min: 0,
                            message: translate("form.error.required"),
                          },
                        ]}
                      >
                        <InputNumber {...currencyInputProps} />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      {/* One labeled group for value + unit, matching the
                          membership form's Masa Berlaku field, with no
                          separate Satuan column header. */}
                      <Form.Item
                        label={translate("label.validity_period")}
                        required
                      >
                        <Flex gap={8}>
                          <div style={{ flex: 1 }}>
                            <Form.Item
                              name={[field.name, "expiry_value"]}
                              noStyle
                              rules={[
                                {
                                  required: true,
                                  type: "number",
                                  min: 1,
                                  message: translate("form.error.required"),
                                },
                              ]}
                            >
                              <InputNumber style={{ width: "100%" }} min={1} />
                            </Form.Item>
                          </div>
                          <div style={{ flex: 1 }}>
                            <Form.Item
                              name={[field.name, "expiry_unit"]}
                              noStyle
                              rules={[{ required: true, message: "" }]}
                            >
                              <Select
                                style={{ width: "100%" }}
                                options={expiryUnitOptions()}
                              />
                            </Form.Item>
                          </div>
                        </Flex>
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          // Removing a tier is destructive, so the icon
                          // carries the token red (colorError) rather than
                          // reading as neutral ink.
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const tierId = form.getFieldValue([
                              "tiers",
                              field.name,
                              "id",
                            ]);
                            const soldCount = tierId
                              ? (soldCountByTierId.get(tierId) ?? 0)
                              : 0;
                            if (soldCount > 0) {
                              showDestructiveConfirm({
                                title: translate(
                                  "product_catalog.tier_remove_sold_title",
                                ),
                                message: translate(
                                  "product_catalog.tier_remove_sold_content",
                                ),
                                confirmLabel: translate(
                                  "product_catalog.tier_remove_sold_confirm",
                                ),
                                onConfirm: () => remove(field.name),
                              });
                            } else {
                              remove(field.name);
                            }
                          }}
                        />
                      )}
                    </Col>
                  </Row>
                  {tierShort && (
                    <div
                      style={{
                        color: colors.red,
                        fontSize: textPx(13),
                        marginTop: -16,
                        marginBottom: 12,
                      }}
                    >
                      {translateWithVars("product_catalog.tier_below_class_cost", {
                        n: dearestCost,
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Solid ink on off-white text, reusing the design
                system's single source for that treatment (a dashed
                block button vanished against the card). Small +
                alignSelf keeps it a compact
                bottom-left action rather than a full-width slab. */}
            <ConfigProvider theme={modalButtonsTheme}>
              <Button
                size="small"
                icon={<PlusOutlined />}
                // size="small" picks up borderRadiusSM and a 7px
                // paddingInlineSM, which reads cramped inside a full
                // pill; restate the pill radius and match the 15px
                // inline padding the sibling footer buttons use.
                style={{
                  alignSelf: "flex-start",
                  marginTop: 4,
                  borderRadius: 9999,
                  paddingInline: 15,
                }}
                onClick={() =>
                  add({
                    expiry_value: 30,
                    expiry_unit: ExpiryUnit.Day,
                  })
                }
              >
                {translate("product_catalog.add_tier")}
              </Button>
            </ConfigProvider>
            {/* Outside a Form.Item the list inherits plain ink, so
                it is coloured here to read as the error it is. */}
            <div style={{ color: colors.red }}>
              <Form.ErrorList errors={errors} />
            </div>
          </Flex>
        );
      }}
    </Form.List>

    {/* ONE control for the whole product, not one per tier.
        Tiers are the same offering at different credit counts, so
        "5 sessions shareable but 10 sessions not" is not a distinction
        a studio wants; a per-tier switch also rendered between rows and
        read as belonging to the next tier. The value is still stored on
        every tier (a purchase points at a tier, and legacy tiers have
        no product), so submit writes it across all of them. */}
    {(
      <>
        <FormSection title={translate("product_catalog.settings_section")} />
        {/* Helper text via `extra`, so it renders BELOW the switch,
            matching the Promo Package toggle above. Both toggles sit on
            this same form: with the description above one and below the
            other, the two switches land at different offsets from their
            labels and stop reading as a scannable column. */}
        <Form.Item
          label={translate("package_sharing.tier_shareable")}
          extra={
            // marginTop, unlike the promo hint: this row carries the
            // Maks. pengguna input, which is taller than a bare switch,
            // so AntD's default extra gap sits too close to the input's
            // border. The extra space makes the gap LOOK the same as
            // promo's, which is the point.
            <span style={{ display: "block", marginTop: 6, color: colors.ink3 }}>
              {translate("package_sharing.tier_shareable_desc")}
            </span>
          }
        >
          <Flex align="center" gap={16}>
            <Form.Item
              name="is_shareable"
              valuePropName="checked"
              noStyle
            >
              <Switch
                aria-label={translate("package_sharing.tier_shareable")}
                onChange={(checked) => {
                  if (!checked) return;
                  // Enabling on a product whose tiers have already been
                  // sold retroactively lets every existing holder add
                  // someone, so it is confirmed rather than silent.
                  const sold = Array.from(
                    soldCountByTierId.values(),
                  ).reduce((a, b) => a + b, 0);
                  if (sold > 0) {
                    showCautionConfirm({
                      title: translate("package_sharing.tier_shareable"),
                      message: translate(
                        "package_sharing.sold_warning",
                      ).replace("{{count}}", String(sold)),
                      confirmLabel: translate("button.confirm"),
                      onConfirm: () => {},
                      // The switch is already visually on, so a dismiss
                      // has to put it back.
                      onCancel: () =>
                        form.setFields([
                          { name: "is_shareable", value: false },
                        ]),
                    });
                  }
                }}
              />
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {() =>
                form.getFieldValue("is_shareable") ? (
                  <Flex align="center" gap={8}>
                    <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
                      {translate("package_sharing.tier_max_members")}
                    </Text>
                    <Form.Item
                      name="max_members"
                      noStyle
                      initialValue={defaultMaxMembers}
                    >
                      <InputNumber
                        min={2}
                        max={5}
                        style={{ width: 80 }}
                        aria-label={translate(
                          "package_sharing.tier_max_members",
                        )}
                      />
                    </Form.Item>
                  </Flex>
                ) : null
              }
            </Form.Item>
          </Flex>
        </Form.Item>
      </>
    )}
  </>
  );

  // ---- Membership layout (prototype "Detail Membership" card) ----
  // Same card as the credit form (padding, no title, footer bottom right).
  const membershipForm = (
    <Card loading={isEdit && isLoading} styles={{ body: { padding: 20 } }}>
      <Form
        form={form}
        layout="vertical"
        requiredMark={requiredMarkAfter}
        onValuesChange={() => setIsFieldChanged(true)}
        // An explicit Form `disabled` resets AntD's DisabledContext, so the
        // ReadOnlyWhenRestricted wrapper alone would leave these fields live.
        disabled={locked || catalogRestricted}
        initialValues={
          isEdit
            ? {}
            : {
                is_promo: false,
                listed_in_client_catalog: true,
                expiry_value: 1,
                expiry_unit: ExpiryUnit.Month,
                // From a class's Membership tab: that class preselected,
                // which implies the selected-classes scope.
                ...(searchParams.get("class_id")
                  ? {
                      access_scope: AccessScope.Selected,
                      class_ids: [searchParams.get("class_id")],
                    }
                  : { access_scope: AccessScope.AllOpen, class_ids: [] }),
              }
        }
      >
        {locked && (
          <Typography.Text
            style={{ display: "block", marginBottom: 16, color: colors.ink3, fontSize: textPx(13) }}
          >
            {translate("product_catalog.promo_locked_note")}
          </Typography.Text>
        )}

        <FormSection first title={translate("product_catalog.membership_info_section")} />
        <Form.Item
          name="name"
          label={translate("label.membership_name")}
          rules={[
            {
              required: true,
              message: `${translate("form.error.input")} ${translate("label.membership_name")} ${translate("form.error.input_end")}`,
            },
          ]}
        >
          <Input disabled={catalogRestricted} />
        </Form.Item>

        <Form.Item name="description" label={translate("label.description")}>
          <Input.TextArea
            placeholder={`${translate("form.placeholders.enter")} ${translate(
              "label.description"
            )}`}
            autoSize={{ minRows: 3, maxRows: 10 }}
          />
        </Form.Item>

        <Form.Item
          name="is_promo"
          label={translate("product_catalog.membership_promo_switch")}
          valuePropName="checked"
          extra={
            <span style={{ color: colors.ink3 }}>
              {translate("product_catalog.membership_promo_switch_hint")}
            </span>
          }
        >
          <Switch disabled={locked || catalogRestricted} />
        </Form.Item>

        {/* A membership is always a single tier (no Form.List here, unlike
            the credit form's tierFields), so the promo date fields don't
            need to coordinate with any tier-visibility logic — just show
            them when the switch is on. */}
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.is_promo !== cur.is_promo}
        >
          {({ getFieldValue }) =>
            getFieldValue("is_promo") && <PromoDateFields blockPastEnd={!locked} />
          }
        </Form.Item>

        <FormSection title={translate("product_catalog.price_section")} />
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="price"
              label={translate("label.price")}
              rules={[
                {
                  required: true,
                  type: "number",
                  min: 0,
                  message: `${translate("form.error.input")} ${translate("label.price")}`,
                },
              ]}
            >
              <InputNumber {...currencyInputProps} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            {/* Two plain side-by-side controls, not Space.Compact — compact's
                merged borders rendered inconsistently here (clipped left edge
                on the number input, detached select). */}
            <Form.Item label={translate("label.validity_period")} required>
              <Flex gap={8}>
                <div style={{ flex: 1 }}>
                  <Form.Item
                    name="expiry_value"
                    noStyle
                    rules={[
                      {
                        required: true,
                        type: "number",
                        min: 1,
                        message: translate("form.error.required"),
                      },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} min={1} />
                  </Form.Item>
                </div>
                <div style={{ flex: 1 }}>
                  <Form.Item name="expiry_unit" noStyle rules={[{ required: true }]}>
                    <Select style={{ width: "100%" }} options={expiryUnitOptions()} />
                  </Form.Item>
                </div>
              </Flex>
            </Form.Item>
          </Col>
        </Row>

        <FormSection title={translate("product_catalog.access_section")} />
        <Form.Item
          name="access_scope"
          label={translate("product_catalog.label_access")}
          rules={[{ required: true }]}
        >
          <Select
            options={[
              {
                value: AccessScope.AllOpen,
                label: translate("product_catalog.access_all_open"),
              },
              {
                value: AccessScope.Selected,
                label: translate("product_catalog.access_selected"),
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.access_scope !== cur.access_scope}
        >
          {({ getFieldValue }) =>
            getFieldValue("access_scope") === AccessScope.Selected && (
              <Form.Item
                name="class_ids"
                label={translate("class")}
                rules={[
                  {
                    required: true,
                    message: `${translate("form.error.multi_select")} ${translate("class")}`,
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={`${translate("form.placeholders.select")} ${translate("label.class_name")}`}
                  loading={classLoading}
                  showSearch
                  optionFilterProp="label"
                  options={classOptions}
                />
              </Form.Item>
            )
          }
        </Form.Item>

        {/* Placed after Akses: Akses answers what the membership covers,
            sharing answers who may use it, so the two read together. Hidden
            entirely unless the studio has membership sharing switched on.
            Its section title matches the credit form's. */}
        <FormSection title={translate("product_catalog.membership_settings_section")} />
        {(
          <Form.Item
            label={translate("package_sharing.membership_shareable")}
            extra={
              // Same clearance as the credit form: this row has the
              // Maks. pengguna input beside the switch.
              <span style={{ display: "block", marginTop: 6, color: colors.ink3 }}>
                {translate("package_sharing.membership_shareable_desc")}
              </span>
            }
          >
            <Flex align="center" gap={16}>
              <Form.Item name="is_shareable" valuePropName="checked" noStyle>
                <Switch
                  aria-label={translate("package_sharing.membership_shareable")}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() =>
                  form.getFieldValue("is_shareable") ? (
                    <Flex align="center" gap={8}>
                      <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
                        {translate("package_sharing.tier_max_members")}
                      </Text>
                      <Form.Item
                        name="max_members"
                        noStyle
                        initialValue={defaultMaxMembers}
                      >
                        <InputNumber
                          min={2}
                          max={5}
                          style={{ width: 80 }}
                          aria-label={translate(
                            "package_sharing.tier_max_members",
                          )}
                        />
                      </Form.Item>
                    </Flex>
                  ) : null
                }
              </Form.Item>
            </Flex>
          </Form.Item>
        )}

        {/* Storefront listing; same Form-field behaviour as the credit form,
            in the same section as sharing. */}
        <Form.Item
          name="listed_in_client_catalog"
          label={translate("product_catalog.membership_client_visibility_switch")}
          valuePropName="checked"
          extra={
            <span style={{ color: colors.ink3 }}>
              {translate("product_catalog.membership_client_visibility_hint")}
            </span>
          }
        >
          <Switch />
        </Form.Item>

        {/* Same footer as the credit form: bottom right, primary first, then
            the soft-red delete on edit (which replaces Batal) or Batal on
            create. */}
        <Flex justify="flex-end" gap={10} style={{ marginTop: 20 }}>
          <Button
            type="primary"
            loading={creating || updating}
            disabled={(isEdit && !isFieldChanged) || catalogRestricted || anyTierShort}
            onClick={onSubmit}
          >
            {isEdit
              ? translate("button.save_changes")
              : translate("button.confirm")}
          </Button>
          {isEdit && (
            <Button disabled={isLoading || catalogRestricted} onClick={handleDuplicate}>
              {translate("product_catalog.duplicate_button")}
            </Button>
          )}
          {isEdit ? (
            <Button
              disabled={catalogRestricted}
              // Inline colours win over AntD's disabled styling, so a restricted
              // delete drops the soft-red tint and greys out like its neighbours.
              style={catalogRestricted ? undefined : softDeleteStyle}
              onClick={confirmDelete}
            >
              {deleteLabel}
            </Button>
          ) : (
            <Button onClick={() => backToCatalog(!!product?.is_promo)}>{translate("button.cancel")}</Button>
          )}
        </Flex>
      </Form>
    </Card>
  );

  // ---- Credit layout (tier editor) ----
  const creditForm = (
    <Card loading={isEdit && isLoading} styles={{ body: { padding: 20 } }}>
      <Form
        form={form}
        layout="vertical"
        // Red asterisk AFTER the label, as on the membership form.
        requiredMark={requiredMarkAfter}
        onValuesChange={() => setIsFieldChanged(true)}
        // An explicit Form `disabled` resets AntD's DisabledContext, so the
        // ReadOnlyWhenRestricted wrapper alone would leave these fields live.
        disabled={locked || catalogRestricted}
        initialValues={
          isEdit
            ? {}
            : {
                is_promo: false,
                listed_in_client_catalog: true,
                class_ids: searchParams.get("class_id")
                  ? [searchParams.get("class_id")]
                  : [],
                tiers: [{ expiry_value: 30, expiry_unit: ExpiryUnit.Day }],
              }
        }
      >
        {locked && (
          <Typography.Text
            style={{ display: "block", marginBottom: 16, color: colors.ink3, fontSize: textPx(13) }}
          >
            {translate("product_catalog.promo_locked_note")}
          </Typography.Text>
        )}

        <FormSection first title={translate("product_catalog.info_section")} />
        <Form.Item
          name="name"
          label={translate("label.name")}
          rules={[
            {
              required: true,
              message: `${translate("form.error.input")} ${translate("label.name")} ${translate("form.error.input_end")}`,
            },
          ]}
        >
          <Input disabled={catalogRestricted} />
        </Form.Item>

        <Form.Item name="description" label={translate("label.description")}>
          <Input.TextArea
            placeholder={`${translate("form.placeholders.enter")} ${translate(
              "label.description"
            )}`}
            autoSize={{ minRows: 3, maxRows: 10 }}
          />
        </Form.Item>

        <Form.Item
          name="is_promo"
          label={translate("product_catalog.promo_switch")}
          valuePropName="checked"
          extra={
            <span style={{ color: colors.ink3 }}>
              {translate("product_catalog.promo_switch_hint")}
            </span>
          }
        >
          <Switch disabled={locked || catalogRestricted} />
        </Form.Item>

        {/* The promo dates sit under their switch, inside the same section,
            as on the membership form (they used to render below Kelas). */}
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.is_promo !== cur.is_promo}
        >
          {({ getFieldValue }) =>
            getFieldValue("is_promo") && <PromoDateFields blockPastEnd={!locked} />
          }
        </Form.Item>

        <FormSection
          title={translate("product_catalog.class_section")}
          subtitle={translate("product_catalog.class_credits_hint")}
        />
        <Form.Item
          name="class_ids"
          label={translate("class")}
          rules={[
            {
              required: true,
              message: `${translate("form.error.multi_select")} ${translate("class")}`,
            },
          ]}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder={`${translate("form.placeholders.select")} ${translate("label.class_name")}`}
            loading={classLoading}
            showSearch
            optionFilterProp="label"
            options={classOptions}
          />
        </Form.Item>

        <ClassCreditList
          classIds={watchedClassIds ?? []}
          classNameById={classNameById}
          costById={classCostById}
        />

        {tierFields}

        {/* Storefront listing. A plain Form field like Paket Promo (commits on
            Simpan Perubahan), so all three toggles on this page behave the
            same way. No explicit `disabled`: it inherits the Form's
            locked/restricted state like the sharing switch above. */}
        <Form.Item
          name="listed_in_client_catalog"
          label={translate("product_catalog.client_visibility_switch")}
          valuePropName="checked"
          extra={
            <span style={{ color: colors.ink3 }}>
              {translate("product_catalog.client_visibility_hint")}
            </span>
          }
        >
          <Switch />
        </Form.Item>

        {/* Same footer as the membership card: bottom right, primary first,
            then the soft-red delete on edit (which replaces Batal) or Batal on
            create. */}
        <Flex justify="flex-end" gap={10} style={{ marginTop: 20 }}>
          <Button
            type="primary"
            loading={creating || updating}
            disabled={(isEdit && !isFieldChanged) || catalogRestricted || anyTierShort}
            onClick={onSubmit}
          >
            {isEdit
              ? translate("button.save_changes")
              : translate("button.confirm")}
          </Button>
          {isEdit && (
            <Button disabled={isLoading || catalogRestricted} onClick={handleDuplicate}>
              {translate("product_catalog.duplicate_button")}
            </Button>
          )}
          {isEdit ? (
            <Button
              disabled={catalogRestricted}
              // Inline colours win over AntD's disabled styling, so a restricted
              // delete drops the soft-red tint and greys out like its neighbours.
              style={catalogRestricted ? undefined : softDeleteStyle}
              onClick={confirmDelete}
            >
              {deleteLabel}
            </Button>
          ) : (
            <Button onClick={() => backToCatalog(!!product?.is_promo)}>{translate("button.cancel")}</Button>
          )}
        </Flex>
      </Form>
    </Card>
  );

  // Create and edit share the form layout: full width for both types (the
  // membership form used to sit in 14 columns beside the old member card).
  const formLayout = (
    <ReadOnlyWhenRestricted capability={AdminCapability.CatalogPricing}>
      {isMembership ? membershipForm : creditForm}
    </ReadOnlyWhenRestricted>
  );

  return (
    <>
      <PageHeading
        title={
          <Flex gap={12} align="center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => backToCatalog(!!product?.is_promo)}
              aria-label={translate("product_catalog.back_to_catalog")}
            />
            {title}
          </Flex>
        }
      />
      {isEdit ? (
        <Tabs
          activeKey={pageTab}
          onChange={setPageTab}
          items={[
            {
              key: "detail",
              label: translate("product_holders.tab_detail"),
              // Mounted even while the other tab is open: the form instance
              // is filled from the product as soon as it loads.
              forceRender: true,
              children: formLayout,
            },
            {
              key: "holders",
              label: holders.isLoading
                ? holdersTitle(isMembership)
                : `${holdersTitle(isMembership)} (${holders.rows.length})`,
              // Outside ReadOnlyWhenRestricted on purpose: the list is
              // navigation, never an edit, so a restricted catalogue leaves
              // it usable.
              children: (
                <ProductHolders
                  holders={holders}
                  isMembership={isMembership}
                  expiringSoonDays={product?.expiring_soon_days}
                  lowCreditThreshold={product?.low_credit_threshold}
                />
              ),
            },
          ]}
        />
      ) : (
        formLayout
      )}
    </>
  );
};

export default PackageProductDetailPage;
